import { EnhancedViewer as ThreeDViewer } from '@partsbadger/threedviewer';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { Button, InputNumber, message, Spin, Form, notification, Alert } from 'antd';
import { Coatings, Delivery, Finishes, Materials, Price, Processes } from '@partsbadger/library';
import PartStore from '../../stores/PartStore';
import QuoteStore from '../../stores/QuoteStore';
import { CheckOutlined, FileUnknownOutlined } from '@ant-design/icons';
import { buildManufacturingTypePayloadForStlPart, isStlFile, STL_MANUFACTURING_TYPE } from '@partsbadger/utils';
import {
    STL_PROCESS_LOCKED_DESCRIPTION,
    STL_PROCESS_LOCKED_MESSAGE,
} from '../../utils/stlManufacturing';

interface Props {
    setEditPart: (value: boolean, getParts: boolean) => void;
    goCheckoutPage: (
        order_items: {
            quantity: number;
            name: string;
            quote_has_product?: number;
            product_description: string;
            unit_price: string;
        }[]
    ) => void;
}

let busy: any = null;

const PartEdit = ({ setEditPart, goCheckoutPage, ...props }: Props) => {
    const [loading, setLoading] = useState(false);
    const [open3DImage, setOpen3dImage] = useState(false);
    const [open3dViewer, setOpen3dViewer] = useState(false);
    const [open2dViewer, setOpen2dViewer] = useState(false);

    const [form] = Form.useForm();

    useEffect(() => {
        setOpen3dViewer(false);
        const timer = setTimeout(() => {
            if (!QuoteStore.line_item?.product?.has_file_3d && QuoteStore.line_item?.product?.has_file_2d) {
                setOpen2dViewer(true);
            } else if (QuoteStore.line_item?.product?.has_file_3d) {
                setOpen3dViewer(true);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [QuoteStore.line_item?.product.id]);

    const stlFileName = QuoteStore.line_item?.product?.attachments?.file3d?.filename;
    const isStlLineItem = isStlFile(stlFileName);

    useEffect(() => {
        const nextManufacturingType = isStlLineItem
            ? STL_MANUFACTURING_TYPE
            : QuoteStore.line_item?.manufacturing_type || 'CNC';

        const stlPayload =
            isStlLineItem && QuoteStore.line_item
                ? buildManufacturingTypePayloadForStlPart(QuoteStore.line_item, STL_MANUFACTURING_TYPE)
                : null;
        const shouldClearMaterial = Boolean(stlPayload?.product);

        const nextValues: Record<string, unknown> = {
            manufacturing_type: nextManufacturingType,
        };

        if (shouldClearMaterial) {
            nextValues.material = undefined;
        }

        if (
            form.getFieldValue('manufacturing_type') !== nextManufacturingType ||
            (shouldClearMaterial && form.getFieldValue('material') != null)
        ) {
            form.setFieldsValue(nextValues);
        }
    }, [
        form,
        isStlLineItem,
        QuoteStore.line_item?.id,
        QuoteStore.line_item?.manufacturing_type,
        QuoteStore.line_item?.product?.material_related,
    ]);

    const handleSubmit = () => {
        form.validateFields()
            .then(async values => {
                const payload = {
                    id: QuoteStore.line_item?.id,
                    product: {
                        material: values['material'].key,
                        coatings: [values['coatings'].key],
                        finishes: [values['finishes'].key],
                    },
                    quantity: values['quantity'],
                    delivery: values['delivery'],
                    manufacturing_type: values['manufacturing_type'],
                };

                if (QuoteStore.line_item?.quote) {
                    setLoading(true);
                    QuoteStore.getPricesLineItem(QuoteStore.line_item.quote, payload).then(() => {
                        setLoading(false);
                    });
                }
            })
            .catch(errorInfo => {
                form.scrollToField(errorInfo.errorFields[0].name);

                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: 'Required fields are empty!',
                    placement: 'topRight',
                });
                setLoading(true);
            });
    };

    const handleChange = () => {
        if (busy) {
            clearTimeout(busy);
        }

        busy = setTimeout(handleSubmit, 400);
    };

    const handleCreateOrder = () => {
        form.validateFields()
            .then(async values => {
                if (QuoteStore.line_item) {
                    setLoading(true);

                    if (
                        form.isFieldsTouched(['material']) ||
                        form.isFieldsTouched(['coatings']) ||
                        form.isFieldsTouched(['finishes']) ||
                        form.isFieldsTouched(['manufacturing_type']) ||
                        form.isFieldsTouched(['delivery'])
                    ) {
                        const utmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');

                        const payload = {
                            line_item_id: QuoteStore.line_item.id,
                            manufacturing_type: values['manufacturing_type'],
                            quantity: values['quantity'],
                            material: values['material'].key,
                            coating: values['coatings'].key,
                            finish: values['finishes'].key,
                            delivery: values['delivery'],
                            unit_price: QuoteStore.line_item.unit_price,
                            total_price: QuoteStore.line_item.total_price,
                            campaign_tracking: utmParams,
                        };

                        setLoading(true);
                        QuoteStore.duplicateItemForRequote(QuoteStore.line_item.quote, payload)
                            .then(data => {
                                goCheckoutPage([
                                    {
                                        quantity: data.quantity,
                                        name: data.name,
                                        quote_has_product: data.id,
                                        product_description: '',
                                        unit_price: data.unit_price,
                                    },
                                ]);
                                setLoading(false);
                            })
                            .catch(err => {
                                message.error('Error');
                                setLoading(false);
                            });
                    } else {
                        goCheckoutPage([
                            {
                                quantity: values['quantity'],
                                name: QuoteStore.line_item.name,
                                quote_has_product: QuoteStore.line_item.id,
                                product_description: '',
                                unit_price: QuoteStore.line_item.unit_price,
                            },
                        ]);
                    }
                }
            })
            .catch(errorInfo => {
                form.scrollToField(errorInfo.errorFields[0].name);

                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: 'Required fields are empty!',
                    placement: 'topRight',
                });
                setLoading(true);
            });
    };

    const handleSubmitReQuote = () => {
        form.validateFields()
            .then(async values => {
                if (QuoteStore.line_item) {
                    setLoading(true);
                    const payload = {
                        line_item_id: QuoteStore.line_item.id,
                        manufacturing_type: values['manufacturing_type'],
                        quantity: values['quantity'],
                        material: values['material'].key,
                        coating: values['coatings'].key,
                        finish: values['finishes'].key,
                        delivery: values['delivery'],
                        unit_price: QuoteStore.line_item.unit_price,
                        total_price: QuoteStore.line_item.total_price,
                        generate_rfq: true,
                    };
                    QuoteStore.duplicateItemForRequote(QuoteStore.line_item.quote, payload)
                        .then(data => {
                            setLoading(false);
                            notification.success({
                                message: 'The quote has been sent.',
                                description: 'A PartsBadger representative will contact you.',
                            });
                            QuoteStore.set_line_item();
                            setEditPart(false, true);
                        })
                        .catch(err => {
                            message.error('Error');
                            setLoading(false);
                        });
                }
            })
            .catch(errorInfo => {
                form.scrollToField(errorInfo.errorFields[0].name);

                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: 'Required fields are empty!',
                    placement: 'topRight',
                });
                setLoading(true);
            });
    };

    const manufacturing_type = isStlLineItem
        ? STL_MANUFACTURING_TYPE
        : form.getFieldValue('manufacturing_type') || QuoteStore.line_item?.manufacturing_type || 'CNC';

    return (
        <div className="max-w-screen-xl mx-auto px-4">
            <div className="-mx-4 flex flex-wrap">
                {QuoteStore?.line_item && (
                    <>
                        <div
                            className="hidden md:block w-full flex flex-col p-4 sm:w-1/1 lg:w-1/2"
                            style={{
                                height: 480,
                            }}
                        >
                            <div className="flex flex-col flex-1" style={{ height: '100%' }}>
                                <div className="flex-1">
                                    {open3DImage &&
                                    QuoteStore.line_item.product.attachments.file3d &&
                                    QuoteStore.line_item.product.attachments.file3d.image ? (
                                        <div className="flex items-center justify-center" style={{ height: '100%' }}>
                                            <div className="max-w-md bg-white rounded-lg shadow-lg">
                                                <img
                                                    style={{ height: '300px' }}
                                                    src={QuoteStore.line_item.product.attachments.file3d.image}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        open3dViewer &&
                                        QuoteStore.line_item.product.attachments.file3d &&
                                        QuoteStore.line_item.product.attachments.file3d.file_3djs && (
                                            <div style={{ height: '300px' }} className="bg-black">
                                                <ThreeDViewer
                                                    url={QuoteStore.line_item.product.attachments.file3d.file_3djs}
                                                />
                                            </div>
                                        )
                                    )}

                                    {open2dViewer && (
                                        <>
                                            {QuoteStore.line_item.product.attachments.files_2d && (
                                                <div style={{ height: '100%' }}>
                                                    {QuoteStore.line_item.product.attachments.files_2d.file
                                                        ?.split('.')
                                                        ?.pop()
                                                        ?.split('?')[0]
                                                        .toLowerCase() == 'pdf' ? (
                                                        <embed
                                                            src={QuoteStore.line_item.product.attachments.files_2d.file}
                                                            type="application/pdf"
                                                            width="100%"
                                                            height={`100%`}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="flex items-center justify-center"
                                                            style={{ height: '100%' }}
                                                        >
                                                            <div className="max-w-md bg-white rounded-lg shadow-lg">
                                                                <FileUnknownOutlined
                                                                    style={{
                                                                        fontSize: 120,
                                                                        display: 'flex',
                                                                        margin: 'auto',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                />{' '}
                                                                <br /> <br />
                                                                This file is not a pdf
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className={'flex justify-center pt-4 space-x-4'}>
                                    {QuoteStore.line_item.product.attachments.files_2d &&
                                        QuoteStore.line_item.product.attachments.file3d &&
                                        QuoteStore.line_item.product.attachments.file3d.file_3djs && (
                                            <>
                                                <Button
                                                    type={'default'}
                                                    className={'rounded-lg'}
                                                    size="small"
                                                    onClick={() => {
                                                        setOpen2dViewer(false);
                                                        setOpen3dViewer(true);
                                                        setOpen3dImage(false);
                                                    }}
                                                >
                                                    {open3dViewer && <CheckOutlined />}
                                                    View in 3D
                                                </Button>
                                            </>
                                        )}
                                    {QuoteStore.line_item.product.attachments.file3d &&
                                        QuoteStore.line_item.product.attachments.files_2d &&
                                        QuoteStore.line_item.product.attachments.files_2d.file && (
                                            <Button
                                                type={'default'}
                                                className={'rounded-lg'}
                                                size="small"
                                                onClick={() => {
                                                    setOpen2dViewer(true);
                                                    setOpen3dViewer(false);
                                                    setOpen3dImage(false);
                                                }}
                                            >
                                                {open2dViewer && <CheckOutlined />}
                                                View in 2D
                                            </Button>
                                        )}
                                </div>
                            </div>
                        </div>

                        <div className="w-full flex flex-col p-4 sm:w-1/1 lg:w-1/2">
                            <div className="flex flex-col flex-1">
                                <div className="flex-1">
                                    <Form className={'form-part'} layout={'vertical'} form={form}>
                                        <div className="max-w-screen-xl mx-auto px-4">
                                            <div className="-mx-4 flex flex-wrap">
                                                <div className="w-full px-4 sm:w-1/1 lg:w-1/2">
                                                    {isStlLineItem ? (
                                                        <Alert
                                                            type="info"
                                                            showIcon
                                                            message={STL_PROCESS_LOCKED_MESSAGE}
                                                            description={STL_PROCESS_LOCKED_DESCRIPTION}
                                                            className="mb-4"
                                                        />
                                                    ) : null}
                                                    <Form.Item
                                                        label={'Process'}
                                                        name="manufacturing_type"
                                                        rules={[{ required: true, message: 'Please select a value' }]}
                                                        initialValue={
                                                            isStlLineItem
                                                                ? STL_MANUFACTURING_TYPE
                                                                : QuoteStore.line_item.manufacturing_type
                                                        }
                                                    >
                                                        <Processes onSelect={handleChange} disabled={isStlLineItem} />
                                                    </Form.Item>
                                                </div>
                                                <div className="w-full px-4 sm:w-1/1 lg:w-1/2">
                                                    <Form.Item
                                                        label={'Quantity'}
                                                        name="quantity"
                                                        rules={[{ required: true, message: 'Please insert a value' }]}
                                                        initialValue={QuoteStore.line_item.quantity}
                                                    >
                                                        <InputNumber
                                                            size="small"
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                            onChange={handleChange}
                                                        />
                                                    </Form.Item>
                                                </div>
                                                <div className="w-full px-4 sm:w-1/1 lg:w-1/2">
                                                    <Form.Item
                                                        label={'Material'}
                                                        name="material"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: 'Please select a value',
                                                            },
                                                        ]}
                                                        initialValue={
                                                            QuoteStore.line_item.product.material_related?.id && {
                                                                key: QuoteStore.line_item.product.material_related.id,
                                                                label:
                                                                    QuoteStore.line_item.product.material_related.name,
                                                            }
                                                        }
                                                    >
                                                        <Materials
                                                            onSelect={handleChange}
                                                            manufacturing_type={manufacturing_type}
                                                            materials={PartStore.materials}
                                                        />
                                                    </Form.Item>
                                                </div>
                                                <div className="w-full px-4 sm:w-1/1 lg:w-1/2">
                                                    <Form.Item
                                                        label={'Coating'}
                                                        name="coatings"
                                                        rules={[{ required: true, message: 'Please select a value' }]}
                                                        initialValue={
                                                            QuoteStore.line_item.product.coating_related?.id && {
                                                                key: QuoteStore.line_item.product.coating_related.id,
                                                                label:
                                                                    QuoteStore.line_item.product.coating_related.name,
                                                            }
                                                        }
                                                    >
                                                        <Coatings
                                                            onSelect={handleChange}
                                                            coatings={PartStore.coatings}
                                                        />
                                                    </Form.Item>
                                                </div>
                                                <div className="w-full px-4 sm:w-1/1 lg:w-1/2">
                                                    <Form.Item
                                                        label={'Surface Finish'}
                                                        name="finishes"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: 'Please select a value',
                                                            },
                                                        ]}
                                                        initialValue={
                                                            QuoteStore.line_item.product.finish_related?.id && {
                                                                key: QuoteStore.line_item.product.finish_related.id,
                                                                label: QuoteStore.line_item.product.finish_related.name,
                                                            }
                                                        }
                                                    >
                                                        <Finishes
                                                            onSelect={handleChange}
                                                            finishes={PartStore.finishes}
                                                        />
                                                    </Form.Item>
                                                </div>
                                                <div className="w-full px-4 sm:w-1/1 lg:w-1/2">
                                                    <Form.Item
                                                        label={'Delivery'}
                                                        name="delivery"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: 'Please select a value',
                                                            },
                                                        ]}
                                                        initialValue={QuoteStore.line_item.delivery}
                                                    >
                                                        <Delivery size="small" onChange={handleChange} />
                                                    </Form.Item>
                                                </div>

                                                <div className="w-full">
                                                    <div className={'flex flex-col justify-between'}>
                                                        <div
                                                            className="flex flex-row justify-between pt-4"
                                                            style={{
                                                                borderTop: 'solid 1px #d9d9d9',
                                                            }}
                                                        >
                                                            <div className="summary-label">Unit Price</div>
                                                            <div className="summary-value">
                                                                <Spin spinning={loading}>
                                                                    {QuoteStore.line_item.safe_auto_quote ||
                                                                    QuoteStore.show_prices ? (
                                                                        <>
                                                                            <Price
                                                                                value={QuoteStore.line_item.unit_price}
                                                                            />
                                                                        </>
                                                                    ) : (
                                                                        <div className={'text-sm font-light'}>
                                                                            Needs Review
                                                                        </div>
                                                                    )}
                                                                </Spin>
                                                            </div>
                                                        </div>

                                                        <div className="flex flex-row justify-between  mt-2 pt-4">
                                                            <div className="font-bold text-xl">Total Price</div>
                                                            <div className="font-bold text-xl">
                                                                <Spin spinning={loading}>
                                                                    {QuoteStore.line_item.safe_auto_quote ||
                                                                    QuoteStore.show_prices ? (
                                                                        <>
                                                                            <Price
                                                                                value={QuoteStore.line_item.total_price}
                                                                            />
                                                                        </>
                                                                    ) : (
                                                                        <div className={'text-sm font-light'}>
                                                                            Needs Review
                                                                        </div>
                                                                    )}
                                                                </Spin>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={'flex flex-row w-full mt-6 justify-center space-x-4'}>
                                                    <Button
                                                        type={'default'}
                                                        onClick={() => {
                                                            form?.resetFields();
                                                            QuoteStore.set_line_item();
                                                            setOpen3dImage(false);
                                                            setOpen3dViewer(false);
                                                            setOpen2dViewer(false);
                                                            setEditPart(false, false);
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    {QuoteStore.line_item.safe_auto_quote || QuoteStore.show_prices ? (
                                                        <Button
                                                            loading={loading}
                                                            type="primary"
                                                            onClick={() => {
                                                                handleCreateOrder();
                                                            }}
                                                        >
                                                            <span className="mr-1">Order Now</span>{' '}
                                                            <Price value={QuoteStore.line_item.total_price} />
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            type={'primary'}
                                                            onClick={() => {
                                                                setOpen3dImage(false);
                                                                setOpen3dViewer(false);
                                                                setOpen2dViewer(false);
                                                                handleSubmitReQuote();
                                                            }}
                                                            loading={loading}
                                                        >
                                                            Submit for Engineer Review
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Form>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default observer(PartEdit);
